/* import __COLOCATED_TEMPLATE__ from './select-all-multiselect.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type { DropDownItem } from './custom/chart-builder/chart-builder-helpers';

interface Signature {
  Element: HTMLElement;
  Args: {
    allColumns: Array<{
      heading: string;
      items: DropDownItem[];
    }>;
    selectedColumns: string[];
    filteredAllColumns: Array<{
      heading: string;
      items: DropDownItem[];
    }>;
    onSelectionChange: (columns: string[]) => void;
    onClose?: () => void;
    hasFilter?: boolean;
    filterPlaceholder?: string;
  };
}

export default class SelectAllMultiselect extends Component<Signature> {
  @service declare intl: IntlService;

  @tracked isOpen = false;

  get selectAllLabel() {
    return this.intl.t('reporting.data-export.select-all-columns');
  }

  get selectAllValue() {
    return 'SELECT_ALL_COLUMNS';
  }

  get filteredAllColumnValues(): string[] {
    if (!this.args.filteredAllColumns) {
      return [];
    }
    return this.args.filteredAllColumns.flatMap((group) => {
      return group.items.map((item) => item.value).filter(Boolean);
    });
  }

  get isAllSelected(): boolean {
    return this.filteredAllColumnValues.every((value) => this.args.selectedColumns.includes(value));
  }

  get isPartiallySelected(): boolean {
    return this.args.selectedColumns.length > 0 && !this.isAllSelected;
  }

  get selectAllItem() {
    return {
      text: this.selectAllLabel,
      value: this.selectAllValue,
      isSelected: this.isAllSelected,
      isIndeterminate: this.isPartiallySelected,
    };
  }

  get groupedItems() {
    let selectAllGroup = {
      heading: '',
      items: [this.selectAllItem],
    };

    return [selectAllGroup, ...this.args.allColumns];
  }

  @action
  onSelectionChange(selectedValues: string[]) {
    let wasSelectAllSelected = this.isAllSelected;
    let isSelectAllSelected = selectedValues.includes(this.selectAllValue);

    if (isSelectAllSelected && !wasSelectAllSelected) {
      // Select all columns - user just clicked "select all"
      this.args.onSelectionChange([...this.filteredAllColumnValues]);
    } else if (!isSelectAllSelected && wasSelectAllSelected) {
      // Deselect all columns - user just unchecked "select all"
      this.args.onSelectionChange(['']);
    } else {
      // Normal selection change - remove select all value if present
      let filteredValues = selectedValues.filter((value) => value !== this.selectAllValue);
      this.args.onSelectionChange(filteredValues);
    }
  }

  @action
  onClose() {
    this.isOpen = false;
    this.args.onClose?.();
  }

  @action
  onOpen() {
    this.isOpen = true;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::SelectAllMultiselect': typeof SelectAllMultiselect;
    'reporting/select-all-multiselect': typeof SelectAllMultiselect;
  }
}
